/**
 * Reactions on a live's replay (`prototype/live-sessions.html`, screens 5 and
 * 6), drawn into the Watch page's own player: where people reacted as a strip
 * over the bar, the live's solid and the replay's paler on top, the three
 * busiest moments marked, the live's reactions rising again at their second,
 * and the seven to react with.
 *
 * A mark shows its card on hover and plays from just before it on a click.
 * A tap on a reaction floats at once, joins the strip at the
 * second the viewer is on, and goes to the API with the others every few
 * seconds, and whatever is left when they leave.
 */
import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  freshBucket,
  type ReactionKind,
  type ReactionMoment,
  type ReactionTimeline,
  REPLAY_SEND_MS,
  takeTap,
} from "../../shared/live-reactions";
import { FloatLane } from "../live/room/FloatLane";
import { fmt } from "../lib/when";
import type { Lane } from "../live/room/lane";
import { addCounts, byCount, countOf, REACTION_OF } from "../live/room/reactions";
import { ReactionButtons } from "./Reactions";
import {
  binAt,
  ECHO_PER_SEC,
  echoesAt,
  echoKind,
  MARKS,
  PLAY_BEFORE_SEC,
  ReplayTaps,
  type ReplayTap,
  stripPaths,
} from "./replayMaths";

/** What the player needs to draw a replay's reactions. */
export interface ReplayReactions {
  timeline: ReactionTimeline;
  /** Send a batch of taps; `leaving` when the page is going and the request must outlive it. */
  send: (taps: ReplayTap[], leaving: boolean) => Promise<unknown>;
}

/** The parts, each put where the player draws that part of itself. */
interface ReplayReactionParts {
  /** Over the scrub. */
  strip: ReactNode;
  /** In the picture. */
  lane: ReactNode;
  /** In the bar. */
  buttons: ReactNode;
  /** The card for one of the busiest moments, in the bar. */
  card: ReactNode;
  /** "Playing the moment at 29:48", in the picture on a desktop. */
  note: ReactNode;
}

const mmss = (sec: number): string => {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const CARD_W = 300;
const NOTE_MS = 2600;

/** What any replay page needs from the reactions, whatever it draws: the lane, a tap, and the bins with its own taps. */
interface ReplayReactionCore {
  lane: RefObject<Lane | null>;
  tap: (kind: ReactionKind) => void;
  /** The replay's taps per bin, this page's own added at once. */
  replay: number[];
}

/**
 * The reactions' working part, for any replay page: a tap floats at once and joins the strip at the second the
 * viewer is on; the taps go to the API every few seconds and whatever is left as the page goes; and the live's
 * reactions rise again as the talk plays across their second.
 */
export function useReplayReactionCore(
  reactions: ReplayReactions | undefined,
  player: { cur: number; playing: boolean },
): ReplayReactionCore {
  const timeline = reactions?.timeline ?? null;
  const { cur, playing } = player;
  const lane = useRef<Lane | null>(null);
  const [queue] = useState(() => new ReplayTaps());
  const [bucket] = useState(() => freshBucket(Date.now()));
  const [mine, setMine] = useState<number[]>([]);
  const curNow = useRef(cur);
  curNow.current = cur;

  const tap = useCallback(
    (kind: ReactionKind) => {
      lane.current?.mine(kind);
      if (!timeline || !takeTap(bucket, Date.now())) return;
      const second = Math.floor(curNow.current);
      queue.add({ kind, at: second });
      const b = binAt(second, timeline);
      setMine((was) => {
        const next = was.slice();
        next[b] = (next[b] ?? 0) + 1;
        return next;
      });
    },
    [timeline, bucket, queue],
  );

  // ── the taps, to the API in batches, and the last of them as the page goes ──
  const send = useRef(reactions?.send);
  send.current = reactions?.send;
  const on = timeline !== null;
  useEffect(() => {
    if (!on) return;
    const flush = (leaving: boolean) => {
      do {
        const taps = queue.take();
        const to = send.current;
        if (taps.length === 0 || !to) return;
        void to(taps, leaving).catch(() => {});
      } while (leaving);
    };
    const every = window.setInterval(() => flush(false), REPLAY_SEND_MS);
    // Why hidden first: a phone that switches apps and is then closed never fires `pagehide`
    // (MDN, Window pagehide event, read 19 Sep 2026).
    const hidden = () => {
      if (document.visibilityState === "hidden") flush(true);
    };
    const leave = () => flush(true);
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.clearInterval(every);
      window.removeEventListener("pagehide", leave);
      document.removeEventListener("visibilitychange", hidden);
      flush(true);
    };
  }, [on, queue]);

  // ── the live's reactions, rising again as the talk plays across their second ──
  const last = useRef(cur);
  const echoed = useRef({ second: 0, n: 0 });
  useEffect(() => {
    const from = last.current;
    last.current = cur;
    if (!timeline || !playing) return;
    // A jump crosses nothing: only seconds the talk played through rise again.
    if (cur - from <= 0 || cur - from > 2) return;
    for (let sec = Math.floor(from) + 1; sec <= Math.floor(cur); sec++) {
      const n = echoesAt(timeline, sec, Math.random());
      for (let j = 0; j < n; j++) {
        const wall = Math.floor(Date.now() / 1000);
        if (echoed.current.second !== wall) echoed.current = { second: wall, n: 0 };
        if (echoed.current.n >= ECHO_PER_SEC) return;
        echoed.current.n += 1;
        lane.current?.float({ kind: echoKind(timeline, sec, Math.random()), look: "echo", delayMs: j * 420, shift: 0 });
      }
    }
  }, [cur, playing, timeline]);

  const replay = useMemo(() => {
    if (!timeline) return [];
    const out = timeline.replay.map((n, i) => n + (mine[i] ?? 0));
    for (let i = timeline.replay.length; i < mine.length; i++) out[i] = mine[i] ?? 0;
    return out;
  }, [timeline, mine]);
  return { lane, tap, replay };
}

export function useReplayReactions(
  reactions: ReplayReactions | undefined,
  player: {
    cur: number;
    total: number;
    playing: boolean;
    /** Jump the talk and play it. */
    playFrom: (sec: number) => void;
  },
): ReplayReactionParts | null {
  const timeline = reactions?.timeline ?? null;
  const { cur, total, playing, playFrom } = player;
  const { lane, tap, replay } = useReplayReactionCore(reactions, { cur, playing });
  const [open, setOpen] = useState<{ moment: ReactionMoment; left: number } | null>(null);
  const [tip, setTip] = useState<{ x: number; words: string } | null>(null);
  const [note, setNote] = useState("");

  // ── the note ──
  useEffect(() => {
    if (!note) return;
    const t = window.setTimeout(() => setNote(""), NOTE_MS);
    return () => window.clearTimeout(t);
  }, [note]);

  if (!timeline) return null;

  const length = total > 1 ? total : timeline.lengthSec;
  const strip = stripPaths(timeline.live, replay, { bin: timeline.bin, lengthSec: length, group: 1 });
  const marks = timeline.moments.slice(0, MARKS);
  const pct = (sec: number) => `${Math.max(0, Math.min(100, (sec / length) * 100))}%`;

  const showCard = (moment: ReactionMoment, pin: HTMLElement) => {
    const bar = pin.closest(".w-controls");
    if (!bar) return;
    const box = bar.getBoundingClientRect();
    const p = pin.getBoundingClientRect();
    const x = p.left + p.width / 2 - box.left;
    setOpen({ moment, left: Math.max(8, Math.min(box.width - CARD_W - 8, x - CARD_W / 2)) });
  };
  const play = (moment: ReactionMoment) => {
    playFrom(Math.max(0, moment.at - PLAY_BEFORE_SEC));
    setNote(`Playing the moment at ${mmss(moment.at)}`);
  };

  const hover = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse" || (e.target as Element).closest(".w-rx-pin")) {
      setTip(null);
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    const b = binAt(x * length, timeline);
    const n = (timeline.live[b] ?? 0) + (replay[b] ?? 0);
    setTip({ x, words: `${mmss(b * timeline.bin)} · ${fmt(n)} reaction${n === 1 ? "" : "s"}` });
  };

  const stripEl = (
    <div className="w-rx-heat" onPointerMove={hover} onPointerLeave={() => setTip(null)}>
      <svg viewBox={`0 0 ${strip.width} 100`} preserveAspectRatio="none" aria-hidden="true">
        <path className="w-rx-hl" d={strip.live} />
        <path className="w-rx-hr" d={strip.replay} />
      </svg>
      {marks.map((m) => {
        const mix = addCounts(m.live, m.replay);
        const top = byCount(mix)[0]?.[0] ?? "love";
        const people = m.people.live + m.people.replay;
        return (
          <button
            key={m.at}
            type="button"
            className={open?.moment === m ? "w-rx-pin w-on" : "w-rx-pin"}
            style={{ left: pct(m.at) }}
            aria-label={`${mmss(m.at)}, ${people} ${people === 1 ? "person" : "people"} reacted. Play from here`}
            onPointerEnter={(e) => e.pointerType === "mouse" && showCard(m, e.currentTarget)}
            onPointerLeave={() => setOpen(null)}
            onFocus={(e) => showCard(m, e.currentTarget)}
            onBlur={() => setOpen(null)}
            onClick={(e) => {
              e.stopPropagation();
              play(m);
            }}
          >
            <span aria-hidden="true">{REACTION_OF[top].emoji}</span>
          </button>
        );
      })}
      {tip ? (
        <span className="w-rx-htip w-num" style={{ left: `${tip.x * 100}%` }}>
          {tip.words}
        </span>
      ) : null}
    </div>
  );

  return {
    strip: stripEl,
    lane: <FloatLane lane={lane} cap={10} className="w-rx-lane" />,
    buttons: <ReactionButtons className="w-rx-bar" onTap={tap} labels />,
    card: open ? (
      <div className="w-rx-card w-rx-info" style={{ left: open.left }}>
        <MomentInfo moment={open.moment} />
      </div>
    ) : null,
    note: note ? <div className="w-rx-note" role="status">{note}</div> : null,
  };
}

/** One of the busiest moments: its second and chapter, what people sent, the words, and where they came from. */
function MomentInfo({ moment }: { moment: ReactionMoment }): ReactNode {
  const mix = addCounts(moment.live, moment.replay);
  return (
    <>
      <p className="w-rx-ct">
        <b className="w-num">{mmss(moment.at)}</b>
        {moment.chapter ? <span>{moment.chapter}</span> : null}
      </p>
      <p className="w-rx-ce">
        {byCount(mix).map(([k, n]) => (
          <span key={k} role="img" aria-label={`${fmt(n)} ${REACTION_OF[k].label}`}>
            <i>{REACTION_OF[k].emoji}</i>
            <span className="w-num">{fmt(n)}</span>
          </span>
        ))}
      </p>
      {moment.words ? <q>{moment.words}</q> : null}
      <p className="w-rx-cs w-num">
        {fmt(countOf(moment.live))} in the live · {fmt(countOf(moment.replay))} on the replay
      </p>
      <p className="w-rx-cg">Click to play from here</p>
    </>
  );
}
